import CheckIcon from '@mui/icons-material/Check';
import { Box } from '@mui/material';
import React from 'react';
import { theme } from '../../styles/theme/theme';
import './styles.css';

export interface CustomStepperStep {
  label: string;
  description?: string;
}

export interface CustomStepperProps {
  steps: CustomStepperStep[];
  activeStep: number;
  /**
   * Optional callback when a step is clicked
   * If not provided, steps are not clickable
   */
  onStepClick?: (stepIndex: number) => void;
  /**
   * Optional internal progress for the active step (0-100)
   * Used to show progress within a multi-part step
   */
  internalProgress?: number;
}

export const CustomStepper: React.FC<CustomStepperProps> = ({
  steps,
  activeStep,
  onStepClick,
  internalProgress = 50,
}) => {
  const getStepStatus = (stepIndex: number): 'completed' | 'active' | 'pending' => {
    if (stepIndex < activeStep) return 'completed';
    if (stepIndex === activeStep) return 'active';
    return 'pending';
  };

  const getStepStatusText = (status: 'completed' | 'active' | 'pending'): string => {
    switch (status) {
      case 'completed':
        return 'Completado';
      case 'active':
        return 'En progreso';
      case 'pending':
        return 'Pendiente';
    }
  };

  const renderStepIcon = (stepIndex: number, status: 'completed' | 'active' | 'pending') => {
    if (status === 'completed') {
      return (
        <Box
          sx={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: theme.blueNew,
            borderRadius: '6px',
            padding: '6px 8px',
          }}
        >
          <CheckIcon sx={{ fontSize: 16, color: theme.white }} />
        </Box>
      );
    }

    if (status === 'active') {
      return (
        <Box
          sx={{
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'background.paper',
            border: `2px solid ${theme.blueNew}`,
            borderRadius: '6px',
            padding: '6px 8px',
          }}
        >
          <Box
            sx={{
              width: 12,
              height: 12,
              backgroundColor: theme.blueNew,
              borderRadius: '2px',
            }}
          />
        </Box>
      );
    }

    // Pending
    return (
      <Box
        sx={{
          width: 32,
          height: 32,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.paper',
          border: `2px solid ${theme.grayNew400}`,
          borderRadius: '6px',
          padding: '6px 8px',
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            backgroundColor: theme.grayNew400,
            borderRadius: '2px',
          }}
        />
      </Box>
    );
  };

  const renderProgressBar = (stepIndex: number, status: 'completed' | 'active' | 'pending') => {
    // No progress bar after the last step
    if (stepIndex === steps.length - 1) {
      return null;
    }

    const isCompleted = status === 'completed';
    const isActive = status === 'active';

    // For active step, show progress based on internalProgress
    if (isActive) {
      const progress = Math.min(Math.max(internalProgress, 0), 100);
      return (
        <Box
          component="svg"
          sx={{
            flex: 1,
            height: 3,
          }}
          viewBox="0 0 100 3"
          preserveAspectRatio="none"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect x="0" y="0" width={progress} height="3" rx="1.5" fill="#337AFF" />
          <rect x={progress} y="0" width={100 - progress} height="3" rx="1.5" fill="#AAAFB6" />
        </Box>
      );
    }

    // Completed or pending: full-width single bar that fills available space
    return (
      <Box
        component="svg"
        sx={{
          flex: 1,
          height: 3,
        }}
        viewBox="0 0 100 3"
        preserveAspectRatio="none"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="0"
          y="0"
          width="100"
          height="3"
          rx="1.5"
          fill={isCompleted ? '#337AFF' : '#AAAFB6'}
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '6px',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
      }}
    >
      {steps.map((step, index) => {
        const status = getStepStatus(index);
        const isClickable = onStepClick && index < activeStep;

        return (
          <Box
            key={index}
            sx={{
              flex: 1,
              padding: '16px 12px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'flex-start',
              gap: '16px',
              cursor: isClickable ? 'pointer' : 'default',
            }}
            onClick={() => isClickable && onStepClick(index)}
          >
            {/* Icon and progress bar row */}
            <Box
              sx={{
                width: '100%',
                height: 32,
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {renderStepIcon(index, status)}
              {renderProgressBar(index, status)}
            </Box>

            {/* Text content */}
            <Box
              sx={{
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                gap: '10px',
              }}
            >
              {/* Label */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                <Box
                  component="div"
                  sx={{
                    color: status === 'pending' ? 'text.disabled' : 'text.secondary',
                    fontSize: 12,
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    lineHeight: '20px',
                    letterSpacing: '-0.072px',
                  }}
                >
                  PASO {index + 1}
                </Box>
                <Box
                  component="div"
                  sx={{
                    color: status === 'pending' ? 'text.disabled' : 'text.primary',
                    fontSize: 14,
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    lineHeight: '20px',
                    letterSpacing: '-0.084px',
                  }}
                >
                  {step.label}
                </Box>
              </Box>

              {/* Status text */}
              <Box
                component="div"
                sx={{
                  color:
                    status === 'completed'
                      ? '#00AC77'
                      : status === 'active'
                        ? '#337AFF'
                        : 'text.disabled',
                  fontSize: 14,
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  lineHeight: '20px',
                  letterSpacing: '-0.084px',
                }}
              >
                {getStepStatusText(status)}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};
